/**
 * The Quick Screen: is this deal worth a second hour?
 *
 * ⭐⭐⭐ IT OWNS NO NUMBERS. Every figure it shows is computed by the analyzer's existing helpers
 * and every field it edits is the analyzer's own state. A second model of the same deal is how two
 * screens come to disagree - which is the defect this whole file exists downstream of.
 *
 * ⛔⛔ THE ROWS MUST FOOT, AND THAT IS THE POINT OF `screenLines`. The delivered PDF for 13368 Main
 * Street printed "Effective Gross Revenue $383,024", "Total Operating Expenses $14,555" and "Net
 * Operating Income $349,318" - and 383,024 - 14,555 is 368,469, not 349,318. A 5% vacancy was
 * applied and never shown, so the one table a buyer checks by hand could not be checked by hand.
 * Here every subtraction between the top line and NOI is a row, and a test asserts they sum.
 */

export interface ScreenLine {
  label: string;
  /** Signed: income positive, deductions negative. Summing every line gives NOI. */
  amount: number;
  kind: 'income' | 'deduction' | 'total';
}

export interface ScreenLineInput {
  /** Potential gross income as the analyzer holds it. */
  pgi: number;
  vacancyAmount: number;
  otherIncome: number;
  /** NNN recoveries the analyzer adds on top of PGI (0 when PGI already contains them). */
  reimbursements: number;
  /** Every landlord expense including the management fee, as the analyzer totals them. */
  totalExpenses: number;
  /** The analyzer's in-place NOI. Passed in so this cannot invent a second answer. */
  noi: number;
}

const round = (n: number): number => Math.round(n * 100) / 100;

/**
 * The income statement, top line to NOI, with nothing hidden between them.
 *
 * ⚠️ `noi` is passed IN rather than derived. If the rows ever stop summing to it, that is a real
 * disagreement worth surfacing, not something this function should paper over by recomputing.
 */
export const screenLines = (i: ScreenLineInput): ScreenLine[] => {
  const lines: ScreenLine[] = [
    { label: 'Potential gross income', amount: round(i.pgi), kind: 'income' },
  ];
  if (i.reimbursements !== 0) {
    lines.push({ label: 'Expense reimbursements', amount: round(i.reimbursements), kind: 'income' });
  }
  if (i.otherIncome !== 0) {
    lines.push({ label: 'Other income', amount: round(i.otherIncome), kind: 'income' });
  }
  if (i.vacancyAmount !== 0) {
    lines.push({ label: 'Less vacancy', amount: -round(i.vacancyAmount), kind: 'deduction' });
  }
  lines.push({ label: 'Less operating expenses', amount: -round(i.totalExpenses), kind: 'deduction' });
  lines.push({ label: 'Net operating income', amount: round(i.noi), kind: 'total' });
  return lines;
};

/**
 * Do the rows above the total actually reach it?
 *
 * ⭐⭐ TOLERANCE IS ONE DOLLAR, AND THE OM IS WHY. Escondido Plaza's own statement prints
 * $271,909 + $111,114 - $113,770 and calls the answer $269,254; the lines sum to $269,253. Every
 * published statement rounds each line, so a dollar of drift is arithmetic, not a hidden
 * deduction. ⛔ It stays a DOLLAR rather than a percentage: the thing this exists to catch is the
 * $19,151 vacancy the delivered PDF applied without printing, and no sane tolerance hides that.
 */
export const linesFoot = (lines: ScreenLine[]): boolean => {
  const total = lines.find((l) => l.kind === 'total');
  if (!total) return false;
  const sum = lines.filter((l) => l.kind !== 'total').reduce((a, l) => a + l.amount, 0);
  return Math.abs(sum - total.amount) <= 1;
};

/**
 * What the property is worth at the cap rate you want.
 *
 * ⭐ The question he actually asks first: "at a 6.5 I'd pay what?" Null rather than Infinity when
 * the cap is zero or the NOI is not positive - a screen must not print a price off a deal that
 * earns nothing.
 */
export const impliedPriceAtCap = (noi: number, targetCapPercent: number): number | null => {
  if (!Number.isFinite(noi) || noi <= 0) return null;
  if (!Number.isFinite(targetCapPercent) || targetCapPercent <= 0) return null;
  return (noi / targetCapPercent) * 100;
};

/**
 * How the asking price compares with the price your target cap supports.
 * Negative means the asking price is above what your cap will pay.
 */
export const priceGapAtCap = (
  purchasePrice: number,
  noi: number,
  targetCapPercent: number,
): number | null => {
  const implied = impliedPriceAtCap(noi, targetCapPercent);
  if (implied === null || !Number.isFinite(purchasePrice) || purchasePrice <= 0) return null;
  return implied - purchasePrice;
};
